import { CircleIcon, DotFilledIcon, DownloadIcon } from '@radix-ui/react-icons';
import { useMemo, useState } from 'react';
import type { GameHistoryItem } from '@wx-weiqi/contracts';

import { filterHistoryByDate } from '../domain/game-review-presentation';
import './student-game-history.css';

interface Props {
  readonly games: readonly GameHistoryItem[];
  readonly loading: boolean;
  readonly downloadPendingGameId: string | null;
  readonly onView: (gameId: string) => void;
  readonly onDownload: (gameId: string) => void;
}

/** Presentation only; record access and downloads remain in the existing GameStore. */
export function StudentGameHistoryPanel({
  games,
  loading,
  downloadPendingGameId,
  onView,
  onDownload,
}: Props) {
  const [dateFilter, setDateFilter] = useState('');
  const filteredGames = useMemo(() => filterHistoryByDate(games, dateFilter), [games, dateFilter]);

  return (
    <div className="student-history-panel">
      <div className="student-history-filters" aria-label="按对局日期筛选我的棋谱">
        <label>
          对局日期
          <input
            type="date"
            value={dateFilter}
            disabled={loading}
            onChange={(event) => setDateFilter(event.target.value)}
          />
        </label>
        <button
          type="button"
          className="student-history-reset"
          disabled={dateFilter === '' || loading}
          onClick={() => setDateFilter('')}
        >
          显示全部日期
        </button>
        <span role="status">
          {loading ? '正在读取…' : `显示 ${filteredGames.length} / ${games.length} 盘`}
        </span>
      </div>
      <div className="student-history-scroll" aria-busy={loading}>
        {filteredGames.length === 0 ? (
          <p className="student-history-empty" role="status">
            {loading
              ? '正在读取历史记录…'
              : dateFilter === ''
                ? '暂无已完成对局。'
                : '所选日期没有对局记录。'}
          </p>
        ) : (
          <table className="student-history-table">
            <caption className="student-history-sr-only">我的对局记录</caption>
            <colgroup>
              <col className="student-history-col-players" />
              <col className="student-history-col-date" />
              <col className="student-history-col-board" />
              <col className="student-history-col-result" />
              <col className="student-history-col-actions" />
            </colgroup>
            <thead>
              <tr>
                {['对局双方', '对局时间', '棋盘 / 手数', '结果', '操作'].map((heading) => (
                  <th scope="col" key={heading}>
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredGames.map((game) => {
                const black = game.players.find((player) => player.color === 'BLACK');
                const white = game.players.find((player) => player.color === 'WHITE');
                const date = new Date(game.finishedAt);
                const downloading = downloadPendingGameId === game.gameId;
                const finished = game.status === 'FINISHED';
                return (
                  <tr key={game.gameId}>
                    <td className="student-history-players">
                      <div>
                        <DotFilledIcon
                          className="student-history-stone student-history-stone--black"
                          aria-hidden="true"
                        />
                        <span>
                          <span className="student-history-sr-only">黑方：</span>
                          {black?.displayName ?? '—'}
                        </span>
                      </div>
                      <div>
                        <CircleIcon
                          className="student-history-stone student-history-stone--white"
                          aria-hidden="true"
                        />
                        <span>
                          <span className="student-history-sr-only">白方：</span>
                          {white?.displayName ?? '—'}
                        </span>
                      </div>
                    </td>
                    <td className="student-history-date" data-label="对局时间">
                      <time dateTime={game.finishedAt}>
                        <span>
                          {date.toLocaleDateString('zh-CN', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                        </span>
                        <span>
                          {date.toLocaleTimeString('zh-CN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })}
                        </span>
                      </time>
                    </td>
                    <td className="student-history-board" data-label="棋盘 / 手数">
                      <span>{game.boardSize} 路</span>
                      <span>{game.currentSequence} 手</span>
                    </td>
                    <td
                      className="student-history-result"
                      data-finished={finished}
                      data-label="结果"
                    >
                      <strong>{finished ? '已结束' : '已中止'}</strong>
                      <span>{game.finalResult ?? '—'}</span>
                    </td>
                    <td className="student-history-actions">
                      <div>
                        <button
                          type="button"
                          className="student-history-view"
                          onClick={() => onView(game.gameId)}
                        >
                          查看
                        </button>
                        <button
                          type="button"
                          className="student-history-download"
                          disabled={!finished || downloading}
                          onClick={() => onDownload(game.gameId)}
                        >
                          <DownloadIcon aria-hidden="true" />
                          {downloading ? '下载中…' : '下载 SGF'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
